import { Duration } from 'luxon'
import { GBAddressCountry, Location, USAddressCountry } from '../../../../api/models'
import { formatPhoneNumberForDisplay } from '../../../location-management/LocationManagementPage/LocationSettingsTab/phoneNumberUtils'
import { SectionText, toMillis } from './customerCareTimelineUtils'

export interface ContactRow {
  key: string
  name: string
  phone: string
  role: 'Primary contact' | 'Secondary contact'
}

export const getCoverageLabel = (location: Location): string => {
  const outdoorMonitoring = location.monitoring?.serviceStatus?.outdoorMonitoring
  if (!outdoorMonitoring?.hasEligiblePlan) return 'No Coverage'
  const hasConfiguredIntervals = !!outdoorMonitoring?.activeDailyIntervals?.length
  return hasConfiguredIntervals ? 'Overnight' : '24/7'
}

export const pluralize = (value: number, unit: string): string => `${value} ${unit}${value === 1 ? '' : 's'}`

export const formatHandleTime = (createdTimestamp: string, resolutionTimestamp: string): string | undefined => {
  const totalSeconds = Math.round((toMillis(resolutionTimestamp) - toMillis(createdTimestamp)) / 1000)
  if (!Number.isFinite(totalSeconds) || totalSeconds < 1) return undefined

  const { minutes, seconds } = Duration.fromObject({ seconds: totalSeconds }).shiftTo('minutes', 'seconds').toObject() as {
    minutes: number
    seconds: number
  }

  if (!minutes) return pluralize(seconds, 'second')
  if (!seconds) return pluralize(minutes, 'minute')
  return `${minutes} min ${seconds} sec`
}

// Runs from the agent being assigned to the last row the Event Timeline shows, so it ends where the timeline does.
export const getTimelineHandleTime = (fragments: SectionText[], agentAssignedTimestamp: string | undefined): string | undefined => {
  if (!agentAssignedTimestamp || !fragments.length) return undefined
  const lastTime = Math.max(...fragments.map(fragment => toMillis(fragment.time)))
  return formatHandleTime(agentAssignedTimestamp, new Date(lastTime).toISOString())
}

export const buildOtherContacts = (location: Location, phoneCountry: USAddressCountry | GBAddressCountry): ContactRow[] => {
  const primaryContacts = location.contacts?.primaryContacts ?? []
  const secondaryContacts = location.contacts?.secondaryContacts ?? []

  const otherPrimaryContacts: ContactRow[] = primaryContacts.slice(1).map((contact, index) => ({
    key: `primary-${index}`,
    name: contact.name ? `${contact.name.firstName} ${contact.name.lastName}`.trim() : '',
    phone: contact.phone.number ? formatPhoneNumberForDisplay(contact.phone.number, phoneCountry) : '',
    role: 'Primary contact'
  }))

  const secondaryContactRows: ContactRow[] = secondaryContacts.map((contact, index) => ({
    key: `secondary-${index}`,
    name: contact.name,
    phone: contact.phone.number ? formatPhoneNumberForDisplay(contact.phone.number, phoneCountry) : '',
    role: 'Secondary contact'
  }))

  return [...otherPrimaryContacts, ...secondaryContactRows].filter(contact => contact.name !== '' || contact.phone !== '')
}
